import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  People as PeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  Message as MessageIcon,
  Api as ApiIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Timeline as TimelineIcon,
  SmartToy as ChatBotIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const DashboardLayout = () => {
  const theme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(!isMobile);
  const { t, i18n } = useTranslation();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleDrawer = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  React.useEffect(() => {
    document.documentElement.dir = 'ltr';
  }, [i18n.language]);

  // Close mobile drawer when location changes
  React.useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location, isMobile]);

  // Define all possible menu items
  const allMenuItems = {
    flows: { text: t('flows'), icon: <TimelineIcon />, path: '/dashboard/flows' },
    chatBot: { text: t('chat_bot'), icon: <ChatBotIcon />, path: '/dashboard/chat-bot' },
    users: { text: t('users'), icon: <PeopleIcon />, path: '/dashboard/users' },
    gyms: { text: t('gyms'), icon: <FitnessCenterIcon />, path: '/dashboard/gyms' },
    messages: { text: t('messages_logs'), icon: <MessageIcon />, path: '/dashboard/messages' },
    apiDocs: { text: t('api_docs'), icon: <ApiIcon />, path: '/dashboard/api-docs' },
    settings: { text: t('settings'), icon: <SettingsIcon />, path: '/dashboard/profile' },
  };

  // Construct menu items based on user role
  const menuItems = user?.role === 'admin'
    ? [
        allMenuItems.flows,
        allMenuItems.chatBot,
        allMenuItems.users,
        allMenuItems.gyms,
        allMenuItems.messages,
        allMenuItems.apiDocs,
      ]
    : [
        allMenuItems.flows,
        allMenuItems.chatBot,
        allMenuItems.gyms,
        allMenuItems.messages,
      ];

  // Add settings at the bottom for all users
  const bottomMenuItems = [
    allMenuItems.settings,
  ];

  const drawerContent = (
    <Box dir="ltr" sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ width: '100%', textAlign: 'center', mt: 2, mb: 2 }}>
        <Box 
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer' }}
        >
          <img src="/logo.png" alt="Fushflow Logo" style={{ height: 64, margin: '0 auto 16px auto', display: 'block' }} />
        </Box>
      </Box>
      {!isMobile && <Toolbar />}
      <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isMobile || isCollapsed ? 'center' : 'flex-start',
                  px: 2.5,
                }}
                data-hook={`dashboard-menu-item-${item.text.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isMobile || isCollapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(!isMobile && !isCollapsed) && <ListItemText primary={item.text} />}
                {isMobile && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <LanguageSelector
            size="small"
            formDataHook="dashboard-language-select-form"
            selectDataHook="dashboard-language-select"
          />
        </Box>
        <Divider />
        <List>
          {bottomMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isMobile || isCollapsed ? 'center' : 'flex-start',
                  px: 2.5,
                }}
                data-hook="dashboard-settings-item"
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isMobile || isCollapsed ? 0 : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(!isMobile && !isCollapsed) && <ListItemText primary={item.text} />}
                {isMobile && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }} data-hook="dashboard-layout-root">
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            width: isMobile ? '100%' : `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)`,
            ml: isMobile ? 0 : `${isCollapsed ? collapsedDrawerWidth : drawerWidth}px`,
            transition: isMobile ? 'none' : theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
          data-hook="dashboard-appbar"
        >
          <Toolbar data-hook="dashboard-toolbar">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              edge="start"
              sx={{ mr: 2 }}
            >
              {isMobile ? <MenuIcon /> : (isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />)}
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, textAlign: i18n.language === 'he' ? 'right' : 'left' }}>
              {t('admin_dashboard')}
            </Typography>
            <Typography 
              variant="subtitle1" 
              noWrap 
              component="div" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                mr: 2, 
                textAlign: i18n.language === 'he' ? 'right' : 'left',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {user?.first_name} {user?.last_name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              data-hook="dashboard-avatar-btn"
            >
              <Avatar>{user?.email?.[0].toUpperCase()}</Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              onClick={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleLogout} data-hook="dashboard-logout-menu-item">
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('logout')}</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        
        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            anchor="left"
            open={mobileOpen}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: false,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                direction: 'ltr',
              },
            }}
            data-hook="dashboard-mobile-drawer"
            dir="ltr"
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Desktop Drawer */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            anchor="left"
            sx={{
              display: { xs: 'none', md: 'block' },
              width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
                boxSizing: 'border-box',
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
                direction: 'ltr',
              },
            }}
            data-hook="dashboard-desktop-drawer"
            dir="ltr"
          >
            {drawerContent}
          </Drawer>
        )}

        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            pt: 3,
            pb: 3,
            pl: 0,
            pr: 0,
            width: isMobile ? '100%' : `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)`,
            ml: isMobile ? 0 : `${isCollapsed ? collapsedDrawerWidth : drawerWidth}px`,
            transition: isMobile ? 'none' : theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            minWidth: 0,
            boxSizing: 'border-box',
          }}
          data-hook="dashboard-main-content"
        >
          <Toolbar disableGutters />
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardLayout; 